'use client'

import { useState, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconSquareCheck,
  IconSquare,
  IconLoader2
} from '@tabler/icons-react'
import type { EditableDocumentContent, DocumentBlock } from '@/types/documents'
import { useTranslations } from 'next-intl'
import { BodyText } from '@/components/ui/typography'
import { processText } from '@/lib/text-processor'

interface DocumentEditorProps {
  content: EditableDocumentContent
  onContentChange: (content: EditableDocumentContent) => void
  onSave: () => void | Promise<void>
  onCancel: () => void
  isSaving?: boolean
}

export function DocumentEditor({
  content,
  onContentChange,
  onSave,
  onCancel,
  isSaving = false
}: DocumentEditorProps) {
  const t = useTranslations('reader')
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set())

  const selectedCount = useMemo(
    () => content.blocks.filter((b) => b.selected).length,
    [content.blocks]
  )

  const totalCharacters = useMemo(() => {
    return content.blocks
      .filter((b) => b.selected)
      .reduce((sum, block) => {
        return (
          sum +
          block.content.paragraphs.reduce((pSum, para) => {
            return (
              pSum +
              para.sentences.reduce((sSum, sent) => sSum + sent.text.length, 0)
            )
          }, 0)
        )
      }, 0)
  }, [content.blocks])

  // Pre-compute all block data once per content change to avoid recalculation
  const blockData = useMemo(() => {
    return content.blocks.map((block) => {
      const charCount = block.content.paragraphs.reduce(
        (sum, para) =>
          sum +
          para.sentences.reduce((sSum, sent) => sSum + sent.text.length, 0),
        0
      )

      const fullText = block.content.paragraphs
        .map((para) => para.sentences.map((sent) => sent.text).join(''))
        .join('\n\n')

      const firstLine = fullText.split('\n')[0]
      const firstLineDisplay =
        firstLine.length > 80 ? firstLine.slice(0, 80) + '...' : firstLine

      return {
        block,
        charCount,
        firstLine: firstLineDisplay,
        fullPreview: fullText
      }
    })
  }, [content.blocks])

  // Memoize all handlers with useCallback to prevent unnecessary rerenders
  const handleToggleSelect = useCallback(
    (blockId: string) => {
      const updatedBlocks = content.blocks.map((block) =>
        block.id === blockId ? { ...block, selected: !block.selected } : block
      )
      onContentChange({ ...content, blocks: updatedBlocks })
    },
    [content, onContentChange]
  )

  const handleSelectAll = useCallback(() => {
    const updatedBlocks = content.blocks.map((block) => ({
      ...block,
      selected: true
    }))
    onContentChange({ ...content, blocks: updatedBlocks })
  }, [content, onContentChange])

  const handleDeselectAll = useCallback(() => {
    const updatedBlocks = content.blocks.map((block) => ({
      ...block,
      selected: false
    }))
    onContentChange({ ...content, blocks: updatedBlocks })
  }, [content, onContentChange])

  const handleDelete = useCallback(
    (blockId: string) => {
      const updatedBlocks = content.blocks.filter(
        (block) => block.id !== blockId
      )
      onContentChange({ ...content, blocks: updatedBlocks })
      if (editingBlockId === blockId) {
        setEditingBlockId(null)
        setEditText('')
      }
    },
    [content, onContentChange, editingBlockId]
  )

  const handleStartEdit = useCallback((block: DocumentBlock) => {
    const text = block.content.paragraphs
      .map((para) => para.sentences.map((sent) => sent.text).join(''))
      .join('\n\n')
    setEditText(text)
    setEditingBlockId(block.id)
  }, [])

  const handleSaveEdit = useCallback(() => {
    if (!editingBlockId) return

    const updatedBlocks = content.blocks.map((block) => {
      if (block.id === editingBlockId) {
        const processedContent = processText(editText)
        return { ...block, content: processedContent }
      }
      return block
    })

    onContentChange({ ...content, blocks: updatedBlocks })
    setEditingBlockId(null)
    setEditText('')
  }, [editingBlockId, editText, content, onContentChange])

  const handleCancelEdit = useCallback(() => {
    setEditingBlockId(null)
    setEditText('')
  }, [])

  const handleAddBlock = useCallback(() => {
    const newBlock: DocumentBlock = {
      id: `block-${Date.now()}`,
      type: 'block',
      content: { paragraphs: [] },
      selected: true
    }
    onContentChange({
      ...content,
      blocks: [...content.blocks, newBlock]
    })
    setEditText('')
    setEditingBlockId(newBlock.id)
  }, [content, onContentChange])

  const getBlockLabel = useCallback((block: DocumentBlock, index: number) => {
    if (block.type === 'page' && block.pageNumber) {
      return `Page ${block.pageNumber}`
    }
    return `Block ${index + 1}`
  }, [])

  const toggleExpand = useCallback((blockId: string) => {
    setExpandedBlocks((prev) => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(blockId)) {
        newExpanded.delete(blockId)
      } else {
        newExpanded.add(blockId)
      }
      return newExpanded
    })
  }, [])

  const handleEditTextChange = useCallback((text: string) => {
    setEditText(text)
  }, [])

  const handleSaveClick = useCallback(async () => {
    try {
      const result = onSave()
      if (result instanceof Promise) {
        await result
      }
    } catch (error) {
      console.error('Error saving document:', error)
    }
  }, [onSave])

  const allSelected = useMemo(
    () => content.blocks.length > 0 && content.blocks.every((b) => b.selected),
    [content.blocks]
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">
            {t('editDocument', { defaultValue: 'Edit Document' })}
          </h3>
          <BodyText className="text-muted-foreground text-xs">
            {t('editDocumentDescription', {
              defaultValue:
                'Review and edit your document. Select which pages or blocks to include.'
            })}
          </BodyText>
        </div>
        <div className="flex items-center gap-2">
          {content.blocks.length > 0 && (
            <>
              {allSelected ? (
                <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                  <IconSquare className="mr-2 h-4 w-4" />
                  {t('deselectAll', { defaultValue: 'Deselect All' })}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  <IconSquareCheck className="mr-2 h-4 w-4" />
                  {t('selectAll', { defaultValue: 'Select All' })}
                </Button>
              )}
            </>
          )}
          <Button variant="outline" size="sm" onClick={handleAddBlock}>
            <IconPlus className="mr-2 h-4 w-4" />
            {t('addBlock', { defaultValue: 'Add Block' })}
          </Button>
        </div>
      </div>

      <div className="bg-muted/20 flex max-h-[500px] flex-col gap-2 overflow-y-auto rounded-lg border p-2">
        {blockData.map((data, index) => {
          const isEditing = editingBlockId === data.block.id
          const isExpanded = expandedBlocks.has(data.block.id)

          return (
            <div
              key={data.block.id}
              className="bg-card hover:bg-muted/50 rounded-lg border p-3 transition-colors"
            >
              {isEditing ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={data.block.selected}
                        onCheckedChange={() =>
                          handleToggleSelect(data.block.id)
                        }
                      />
                      <span className="text-sm font-medium">
                        {getBlockLabel(data.block, index + 1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleSaveEdit}
                      >
                        <IconCheck className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleCancelEdit}
                      >
                        <IconX className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={editText}
                    onChange={(e) => handleEditTextChange(e.target.value)}
                    className="min-h-32 font-mono text-sm"
                    placeholder={t('editBlockPlaceholder', {
                      defaultValue: 'Edit block content...'
                    })}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      <Checkbox
                        checked={data.block.selected}
                        onCheckedChange={() =>
                          handleToggleSelect(data.block.id)
                        }
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">
                            {getBlockLabel(data.block, index + 1)}
                          </span>
                          {data.block.type === 'page' && (
                            <Badge variant="outline" className="text-xs">
                              {t('page', { defaultValue: 'Page' })}
                            </Badge>
                          )}
                          <BodyText className="text-muted-foreground text-xs">
                            {data.charCount.toLocaleString()}{' '}
                            {t('characters', { defaultValue: 'chars' })}
                          </BodyText>
                        </div>
                        {!isExpanded && (
                          <BodyText className="text-muted-foreground mt-1 line-clamp-1 text-xs">
                            {data.firstLine}
                          </BodyText>
                        )}
                        {isExpanded && (
                          <div className="bg-muted/30 mt-2 rounded border p-2">
                            <BodyText className="text-xs wrap-break-word whitespace-pre-wrap">
                              {data.fullPreview}
                            </BodyText>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => toggleExpand(data.block.id)}
                        title={
                          isExpanded
                            ? t('collapse', { defaultValue: 'Collapse' })
                            : t('expand', { defaultValue: 'Expand' })
                        }
                      >
                        {isExpanded ? (
                          <IconChevronUp className="h-4 w-4" />
                        ) : (
                          <IconChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleStartEdit(data.block)}
                        title={t('edit', { defaultValue: 'Edit' })}
                      >
                        <IconEdit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleDelete(data.block.id)}
                        title={t('delete', { defaultValue: 'Delete' })}
                      >
                        <IconTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {content.blocks.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <BodyText className="text-muted-foreground text-sm">
              {t('noBlocks', {
                defaultValue: 'No blocks yet. Add a block to get started.'
              })}
            </BodyText>
          </div>
        )}
      </div>

      <div className="bg-muted/30 flex items-center justify-between rounded-lg border px-4 py-3">
        <div className="flex items-center gap-4">
          <BodyText className="text-sm font-medium">
            {selectedCount} / {content.blocks.length}{' '}
            {t('selected', { defaultValue: 'selected' })}
          </BodyText>
          <BodyText className="text-muted-foreground text-xs">
            {totalCharacters.toLocaleString()}{' '}
            {t('characters', { defaultValue: 'characters' })}
          </BodyText>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            size="sm"
            onClick={handleSaveClick}
            disabled={
              selectedCount === 0 || editingBlockId !== null || isSaving
            }
          >
            {isSaving ? (
              <>
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('saving', { defaultValue: 'Saving...' })}
              </>
            ) : (
              t('saveDocument', { defaultValue: 'Save Document' })
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
